import dayjs, { Dayjs } from 'dayjs';

import { Locale, ReservationTextType } from 'app/generated/hygraph';
import { AttractionContent } from 'app/hooks/useAttraction';
import {
  AttractionConfig,
  EntitlementReservation,
  LineupAttraction,
  Pass,
  ProductFamily,
  Reservation,
  ReservationModel,
  ReservationOption,
  ReservationRequirement,
  ReservationType,
  TicketCategory,
} from 'app/services/GuestCenterService.types';
import { expandTpl, getTextKey, ReservationTextHelper } from 'app/services/I18n';
import { isAlacarte } from 'app/services/ProductLineupHelper';
import { toYMD } from 'app/utils/time';

export enum ReservationActions {
  ReservePhone = 'reserve-phone',
  ReserveRedirect = 'reserve-redirect',
  Reserve = 'reserve',
  ViewTickets = 'view-tickets',
}

export function isReservable(config: AttractionConfig, lineupAttraction: LineupAttraction) {
  return (
    !config.isClosed &&
    lineupAttraction.reservationModel !== ReservationModel.None &&
    lineupAttraction.reservationRequirement !== ReservationRequirement.NotRequired &&
    (hasTicketingSystem(config) || isRedirect(lineupAttraction))
  );
}

export function hasTicketingSystem(config: Maybe<AttractionConfig>) {
  return !!config?.ticketingSystem;
}

export function isPhone(lineupAttraction: LineupAttraction) {
  return lineupAttraction.reservationModel === ReservationModel.Phone;
}

// when true = model 3/4
export function isRedirect(lineupAttraction: Maybe<LineupAttraction>) {
  return lineupAttraction?.reservationModel === ReservationModel.Redirect;
}

export function redirectURL(
  config: AttractionConfig,
  props?: Record<string, string | number | string[]>
) {
  return expandTpl(config.ticketingSystem?.redirectURL || '', { props });
}

export function phoneNumber(reservationContent: AttractionContent['reservation']): string[] {
  return (
    reservationContent?.texts.find((text) => getTextKey(text) === ReservationTextType.PhoneNumber)
      ?.value ?? ''
  )
    .split('|')
    .map((v) => v.trim());
}

export function userActions(
  config: AttractionConfig,
  lineupAttraction: LineupAttraction
): ReservationActions[] {
  const actions: ReservationActions[] = [];

  if (isPhone(lineupAttraction)) {
    actions.push(ReservationActions.ReservePhone);
  } else if (isReservable(config, lineupAttraction)) {
    actions.push(
      isRedirect(lineupAttraction) ? ReservationActions.ReserveRedirect : ReservationActions.Reserve
    );
  }

  if (lineupAttraction.showViewTickets) {
    actions.push(ReservationActions.ViewTickets);
  }

  return actions;
}

export function barcodeTail(barcode: string): string {
  return barcode.slice(-4);
}

export function prettyBarcode(barcode: string): string {
  return barcode.replace(/(\d{4})/g, '$1 ');
}

export function ticketingDaysOutMax(config?: AttractionConfig): number | undefined {
  const daysOutMax = config?.ticketingSystem?.search?.daysOutMax;
  return daysOutMax === -1 ? undefined : daysOutMax;
}

export function ticketingDaysOutMin(config?: AttractionConfig): number {
  const daysOutMin = config?.ticketingSystem?.search?.daysOutMin;
  return daysOutMin ?? 0;
}

export function allowHold(config?: AttractionConfig): boolean {
  return !!config?.ticketingSystem?.reserve?.allowHold;
}

export function hasOptions(
  config: AttractionConfig | undefined,
  familyCode: ProductFamily
): boolean {
  return getOptions(config, familyCode).length > 0;
}

export function getOptions(
  config: AttractionConfig | undefined,
  familyCode: ProductFamily
): ReservationOption[] {
  const allOptions = config?.ticketingSystem?.options || [];
  return allOptions.filter((option) => option.familyCode === familyCode);
}

export function getOptionType(
  config: AttractionConfig | undefined,
  familyCode: ProductFamily
): ReservationOption['optionType'] {
  return getOptions(config, familyCode)[0]?.optionType ?? '';
}

export function hasTimeBasedOptions(
  config: AttractionConfig | undefined,
  familyCode: ProductFamily
): boolean {
  const options = getOptions(config, familyCode);
  return options.some((option) => option.overrideReservationType === ReservationType.Time);
}

export function allOptionsAreTimeBased(
  config: AttractionConfig | undefined,
  familyCode: ProductFamily
): boolean {
  const options = getOptions(config, familyCode);

  // If there are no options, return false
  if (options.length === 0) return false;

  // Check if ALL options are time-based
  return options.every((option) => option.overrideReservationType === ReservationType.Time);
}

export function allowCancel(config?: AttractionConfig): boolean {
  return config?.ticketingSystem?.cancel?.allowCancel === true;
}
export function allowModify(config?: AttractionConfig): boolean {
  return config?.ticketingSystem?.modify?.allowModify === true;
}
export function getCutoffValue(
  config: AttractionConfig | undefined,
  operation: 'cancel' | 'modify',
  unit: 'minutes' | 'days'
): number {
  let value: number;

  if (operation === 'cancel') {
    value =
      unit === 'minutes'
        ? config?.ticketingSystem?.cancel?.minutesOut ?? 0
        : config?.ticketingSystem?.cancel?.daysOut ?? 0;
  } else {
    value =
      unit === 'minutes'
        ? config?.ticketingSystem?.modify?.modifyMinutesOut ?? 0
        : config?.ticketingSystem?.modify?.modifyDaysOut ?? 0;
  }

  return value < 0 ? 0 : value;
}

export function cancelMinutesOut(config?: AttractionConfig): number {
  return getCutoffValue(config, 'cancel', 'minutes');
}

export function cancelDaysOut(config?: AttractionConfig): number {
  return getCutoffValue(config, 'cancel', 'days');
}

export function modifyMinutesOut(config?: AttractionConfig): number {
  return getCutoffValue(config, 'modify', 'minutes');
}

export function modifyDaysOut(config?: AttractionConfig): number {
  return getCutoffValue(config, 'modify', 'days');
}

export function getCutoffForReservationTime(
  config: AttractionConfig | undefined,
  reservationTime: string
): Dayjs {
  return time(reservationTime, config?.ianaTimezone)
    .subtract(cancelDaysOut(config), 'days')
    .subtract(cancelMinutesOut(config), 'minutes');
}
export function getCutoffForModifyReservationTime(
  config: AttractionConfig | undefined,
  reservationTime: string
): Dayjs {
  return time(reservationTime, config?.ianaTimezone)
    .subtract(modifyDaysOut(config), 'days')
    .subtract(modifyMinutesOut(config), 'minutes');
}
export function canCancel(reservation: EntitlementReservation | undefined): boolean {
  if (!reservation) return false;
  if (reservation.disableCancel) return false;

  const config = reservation.attractionConfig;

  if (config?.isClosed || config?.ticketingSystem?.online === false || !allowCancel(config)) {
    return false;
  }

  const now = dayjs();
  const cutoff = getCutoffForReservationTime(config, reservation.reservationDateTime ?? '');

  return now.isBefore(cutoff);
}

export function canModify(
  config: AttractionConfig | undefined,
  reservation: Pick<Reservation, 'reservationDateTime' | 'disableModify'> | undefined
): boolean {
  if (!reservation) return false;
  if (reservation.disableModify !== false) return false;

  if (config?.isClosed || config?.ticketingSystem?.online === false || !allowModify(config)) {
    return false;
  }

  const now = dayjs();
  const cutoff = getCutoffForModifyReservationTime(config, reservation.reservationDateTime ?? '');

  return now.isBefore(cutoff);
}

export function canShowCancelModifyButtons(
  reservation: EntitlementReservation | undefined
): boolean {
  if (!reservation) return false;

  if (isAlacarte(reservation.productLineup)) {
    return false;
  }

  if (!reservation.attractionOrderNumber) {
    return false;
  }

  return true;
}

export function date(dateTime: string) {
  return dayjs(toYMD(dateTime));
}

export function time(dateTime: string, ianaTimezone?: string) {
  return dayjs(dateTime.split('+')[0]).tz(ianaTimezone, true);
}

export function reservationLabel(reservation?: EntitlementReservation) {
  const ticketCategory = reservation?.hasTicketCategories
    ? reservation.ticketCategories[0]
    : undefined;

  return reservation?.reservationDateTime
    ? time(reservation.reservationDateTime, reservation.attractionConfig?.ianaTimezone).format(
        `dddd, MMM D, YYYY${showReservationTime(reservation, ticketCategory) ? ', h:mma z' : ''}`
      )
    : '';
}

export function hasUsage(reservation?: EntitlementReservation) {
  return reservation?.reservationDateTime
    ? time(reservation.reservationDateTime, reservation.attractionConfig?.ianaTimezone).isBefore(
        dayjs()
      )
    : false;
}

export function hasFutureReservation(reservation?: EntitlementReservation) {
  return reservation?.reservationDateTime
    ? time(reservation.reservationDateTime, reservation.attractionConfig?.ianaTimezone).isAfter(
        dayjs()
      )
    : false;
}

export function getOptionContent(
  reservation: Maybe<EntitlementReservation>,
  {
    type,
    rt,
    ticketCategory,
    locale,
  }: {
    type: 'title' | 'details';
    rt: ReservationTextHelper;
    ticketCategory?: TicketCategory;
    locale?: Locale;
  }
) {
  if (!reservation) return '';

  const optionContentKey = reservation.option?.contentKey;
  const categoryContentKey = ticketCategory?.contentKey;

  if (!optionContentKey && !categoryContentKey) return '';

  const props = {
    locale,
    optionTitle: rt(`${optionContentKey}_title`, { locale }),
    optionDetails: rt(`${optionContentKey}_details`, { locale }),
    categoryTitle: rt(`${categoryContentKey}_title`, { locale }),
    categoryDetails: rt(`${categoryContentKey}_details`, { locale }),
  };

  if (categoryContentKey) {
    if (ticketCategory.displayOptionContent && optionContentKey) {
      const key = `${optionContentKey}_${type}`;
      const value = rt(`${key}_template`, props) || rt(key, props);
      if (value) return value;
    }

    const key = `${categoryContentKey}_${type}`;
    const value = rt(`${key}_template`, props) || rt(key, props);
    if (value) return value;
  }

  if (optionContentKey) {
    return rt(`${optionContentKey}_${type}`, props);
  }

  return '';
}

export function getTicketSummaries(
  reservation: Maybe<EntitlementReservation>,
  { rt, locale }: { rt: ReservationTextHelper; locale?: Locale }
): {
  key: string;
  ticketCategory?: TicketCategory;
  option?: ReservationOption;
  optionTitle: string;
  optionDetails?: string;
}[] {
  if (!reservation) return [];

  return reservation.ticketCategories.length > 0
    ? reservation.ticketCategories.map((ticketCategory) => ({
        key: ticketCategory.categoryKey,
        ticketCategory,
        option: reservation.option,
        optionTitle: getOptionContent(reservation, { type: 'title', rt, ticketCategory, locale }),
        optionDetails: getOptionContent(reservation, {
          type: 'details',
          rt,
          ticketCategory,
          locale,
        }),
      }))
    : [
        {
          key: '$default',
          option: reservation.option,
          optionTitle: getOptionContent(reservation, { type: 'title', rt, locale }),
          optionDetails: getOptionContent(reservation, { type: 'details', rt, locale }),
        },
      ];
}

export function showReservationTime(
  reservation: { type?: ReservationType; option?: Maybe<ReservationOption> },
  ticketCategory?: TicketCategory,
  config?: AttractionConfig,
  familyCode?: ProductFamily
): boolean {
  const reservationType = ticketCategory?.reservationType ?? reservation?.type;
  const overrideType = reservation?.option?.overrideReservationType;

  // If there's an override, it takes precedence
  if (overrideType === ReservationType.Time) return true;
  if (overrideType === ReservationType.Day) return false;

  // If no option is selected yet, check if ALL options are time-based
  // (only show time step from the start if there are no day-based options to choose from)
  if (!reservation?.option && config && familyCode) {
    const allTimeBased = allOptionsAreTimeBased(config, familyCode);
    if (allTimeBased) return true;
  }
  // No override, use the base reservation type
  return reservationType === ReservationType.Time;
}

// Helper functions for working with reservations and tickets
export function getReservationPassBarcodes(reservation: Reservation): string[] {
  return (
    reservation.tickets
      ?.map((ticket) => ticket.passBarcode)
      .filter((barcode): barcode is string => Boolean(barcode)) ?? []
  );
}

export function buildPassBarcodeSet(passes: Pass[]): Set<string> {
  return new Set(passes.map((pass) => pass.barcode));
}
